import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { healthcareApi, type CandidateResponse, type HealthcareRiskMeta } from '../api/client';

function formatDate(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return iso;
  }
}

function riskBadgeClass(riskLevel: string): string {
  const v = (riskLevel || '').toLowerCase();
  if (v === 'high' || riskLevel === '要フォロー') return 'healthcare-badge-high';
  if (v === 'medium' || v === 'med') return 'healthcare-badge-medium';
  if (v === 'low') return 'healthcare-badge-low';
  return 'healthcare-badge-warn';
}

/** リスクレベルツールチップに risk_meta の数値を組み込む */
function buildRiskTooltip(
  base: string,
  meta: HealthcareRiskMeta | null,
): string {
  if (!meta) return base;
  const parts: string[] = [base];
  if (meta.cutoff_value != null) {
    parts.push(`cutoff=${meta.cutoff_value}`);
  }
  if (meta.window_days != null) {
    parts.push(`window=${meta.window_days}d`);
  }
  // p 値は高いほど状態が悪い。旧名（high_below / medium_below）は向きが逆だったため
  // 新名を優先し、旧 dev-api に当たった場合のみ旧名にフォールバックする（BE #82）
  const th = meta.risk_level_thresholds;
  const high = th?.high_at_or_above ?? th?.high_below;
  const medium = th?.medium_at_or_above ?? th?.medium_below;
  if (high != null || medium != null) {
    parts.push(`thresholds: high>=${high ?? '—'} / medium>=${medium ?? '—'}`);
  }
  return parts.join(' | ');
}

/** HLT-S01 フォロー候補一覧（API 連携） */
export default function HealthcareCandidates() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [list, setList] = useState<CandidateResponse[]>([]);
  const [riskMeta, setRiskMeta] = useState<HealthcareRiskMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    healthcareApi
      .getCandidates(token)
      .then((res) => {
        setList(res.data ?? []);
        setRiskMeta(res.risk_meta ?? null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        setList([]);
        setRiskMeta(null);
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  const riskTooltip = buildRiskTooltip(t('healthcare.riskLevelTooltip'), riskMeta);

  return (
    <div className="page healthcare-candidates-page">
      <h1>{t('healthcare.candidates')}</h1>
      <p className="healthcare-intro">{t('healthcare.candidatesIntro')}</p>

      {riskMeta && (
        <details className="healthcare-risk-meta">
          <summary>{t('healthcare.riskMeta.title')}</summary>
          <dl>
            <dt>{t('healthcare.riskMeta.algorithm', { version: riskMeta.algorithm_version })}</dt>
            <dd></dd>
            <dt>
              {t('healthcare.riskMeta.cutoff', {
                value: riskMeta.cutoff_value,
                version: riskMeta.cutoff_version,
              })}
            </dt>
            <dd></dd>
            <dt>{t('healthcare.riskMeta.window', { days: riskMeta.window_days })}</dt>
            <dd></dd>
            <dt>{t('healthcare.riskMeta.dataVolumeMin', { value: riskMeta.data_volume_min })}</dt>
            <dd></dd>
            <dt>
              {t('healthcare.riskMeta.thresholds', {
                high:
                  riskMeta.risk_level_thresholds?.high_at_or_above ??
                  riskMeta.risk_level_thresholds?.high_below ??
                  '—',
                medium:
                  riskMeta.risk_level_thresholds?.medium_at_or_above ??
                  riskMeta.risk_level_thresholds?.medium_below ??
                  '—',
              })}
            </dt>
            <dd></dd>
            {riskMeta.documentation_url && (
              <>
                <dt>
                  <a
                    href={riskMeta.documentation_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('healthcare.riskMeta.documentationLink')}
                  </a>
                </dt>
                <dd></dd>
              </>
            )}
          </dl>
        </details>
      )}

      {error && (
        <div className="healthcare-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {loading && (
        <div className="healthcare-card">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="healthcare-card">
          {list.length === 0 ? (
            <p className="healthcare-empty">{t('healthcare.candidatesEmpty')}</p>
          ) : (
            <table className="healthcare-table">
              <thead>
                <tr>
                  <th scope="col">{t('healthcare.anonId')}</th>
                  <th scope="col">{t('healthcare.latestMcs')}</th>
                  <th scope="col">{t('healthcare.riskLevel')}</th>
                  <th scope="col">{t('healthcare.lastRecordedAt')}</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.anon_id}>
                    <td><code className="healthcare-anon-id">{c.anon_id}</code></td>
                    <td>{c.latest_score != null ? c.latest_score : '—'}</td>
                    <td>
                      <span
                        className={`healthcare-badge ${riskBadgeClass(c.risk_level)}`}
                        title={riskTooltip}
                      >
                        {c.risk_level}
                      </span>
                    </td>
                    <td>{formatDate(c.last_recorded_at)}</td>
                    <td>
                      {/*
                        同意が無い対象者はリンクを出さない（課題 #26）。
                        開いても 403 CONSENT_REQUIRED で操作できないため、空振りのクリックを防ぐ。
                        consent_granted が未指定（BE 未反映）の場合は従来どおりリンクを出す。
                      */}
                      {c.consent_granted === false ? (
                        <span
                          className="healthcare-consent-missing"
                          title={
                            c.missing_consents?.length
                              ? c.missing_consents.map((m) => m.label_ja).join(' / ')
                              : undefined
                          }
                        >
                          {t('healthcare.consentNotGranted')}
                        </span>
                      ) : (
                        <>
                          <Link to={`/healthcare/person/${c.anon_id}`}>{t('healthcare.detail')}</Link>
                          {' · '}
                          <Link to={`/healthcare/person/${c.anon_id}/interview`}>
                            {t('healthcare.interviews')}
                          </Link>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
